'use client';

import { MapSolid, Search } from '@byway/icons';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { SORT_OPTIONS, type SortKeys } from '~/components/AltAccommodationPopUp/src/constants';
import { AriaSelect, SelectItem } from '~/components/AriaSelect';
import { CenteredModal } from '~/components/CenteredModal';
import { Divider } from '~/components/Divider';
import { OverlayDrawer } from '~/components/Drawer';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { useToast } from '~/hooks/useToasts';
import { api } from '~/libs/api';
import { trackEvent } from '~/utils';
import { isBywayFavouriteAccommodation } from '~/utils/accommodationHelpers';
import { trackFunnelEvent } from '~/utils/funnelTracking';
import { logError } from '~/utils/logger';
import { aggregatePax } from '~/utils/paxHelpers/paxHelper';
import { AlternativeAccommodationCard, type AlternativeEntry } from './AlternativeAccommodationCard';
import styles from './AlternativeAccommodationDrawer.module.css';
import { AlternativeAccommodationMapSheetLazy } from './AlternativeAccommodationMapSheetLazy';
import { clearStayFromLiveCheck, invalidateAccommodationQueries } from './accommodationQueryCache';
import { buildAccommodationList, type CurrentStationDistances, filterAccommodationBySearch, sortAccommodationList } from './alternativeAccommodationList';
import { ACCOMMODATION_DRAWERS_STALE_TIME_MS } from './usePrefetchAccommodationDrawersOnInView';
type AlternativeAccommodationDrawerProps = {
  isOpen: boolean;
  close: () => void;
  tripId: string;
  itemId: number;
  placeName: string;
  currentStationDistances?: CurrentStationDistances;
};
export const AlternativeAccommodationDrawer = ({
  isOpen,
  close,
  tripId,
  itemId,
  placeName,
  currentStationDistances
}: Readonly<AlternativeAccommodationDrawerProps>) => {
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');
  const isDesktop = useIsDesktop();
  const toast = useToast();
  const router = useRouter();
  const utils = api.useUtils();
  const isTripUpdating = useIsTripUpdating();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortKeys>();
  const [updatingAccommodationId, setUpdatingAccommodationId] = useState<number | null>(null);
  const [isMapOpen, setIsMapOpen] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSortBy(undefined);
      setIsMapOpen(false);
    }
  }, [isOpen]);
  const handleClose = () => {
    setSearchQuery('');
    setSortBy(undefined);
    setIsMapOpen(false);
    close();
  };
  const handleDesktopModalClose = () => {
    if (isMapOpen) {
      setIsMapOpen(false);
      return;
    }
    handleClose();
  };
  const updateAccommodation = api.trip.updateAccommodation.useMutation();
  const {
    data: alternatives,
    isLoading
  } = api.trip.getAlternativeAccommodation.useQuery({
    journeyId: tripId,
    itemId
  }, {
    ...TRPC_SETTINGS.QUERY_NO_REFETCH,
    enabled: isOpen,
    staleTime: ACCOMMODATION_DRAWERS_STALE_TIME_MS
  });
  const {
    data: overview
  } = api.trip.getOverview.useQuery({
    tripId
  }, {
    ...TRPC_SETTINGS.QUERY_NO_REFETCH,
    enabled: !!tripId
  });
  const {
    adultCount,
    childAges
  } = aggregatePax(overview?.pax?.travellerMixes ?? []);
  const currentAccommodationId = alternatives?.currentAccommodation.id;
  const accommodationList = alternatives ? buildAccommodationList(alternatives, currentStationDistances) : [];
  const filteredAccommodationList = filterAccommodationBySearch(accommodationList, searchQuery);
  const displayedAccommodationList = sortAccommodationList(filteredAccommodationList, sortBy, currentAccommodationId);
  const selectAccommodation = async (accommodation: AlternativeEntry, source: 'list' | 'map') => {
    if (isTripUpdating) return;
    try {
      setUpdatingAccommodationId(accommodation.id);
      startLoader({
        showProgressBar: false
      });
      trackEvent('trip_page_hotel_select', {
        event_category: 'Hotel_Select',
        event_label: 'trip_page_hotel_select',
        favourite: isBywayFavouriteAccommodation(accommodation.ranking),
        source
      });
      const {
        success
      } = await updateAccommodation.mutateAsync({
        journeyId: tripId,
        itemId,
        accommodationId: accommodation.id
      });
      if (!success) {
        toast?.add({
          title: t('accommodation_update_error'),
          variant: 'error',
          position: 'middle'
        }, {
          timeout: 5000
        });
        return;
      }
      clearStayFromLiveCheck(utils, tripId, itemId);
      await Promise.all([utils.trip.getAlternativeAccommodation.invalidate({
        journeyId: tripId,
        itemId
      }), invalidateAccommodationQueries(utils, tripId, itemId)]);
      router.refresh();
      handleClose();
      toast?.add({
        title: t('accommodation_update_success'),
        variant: 'success',
        position: 'middle'
      }, {
        timeout: 5000
      });
    } catch {
      logError('Error updating accommodation');
      toast?.add({
        title: t('accommodation_update_error'),
        variant: 'error',
        position: 'middle'
      }, {
        timeout: 5000
      });
    } finally {
      setUpdatingAccommodationId(null);
      completeProgressBar();
    }
  };
  const ariaLabel = t('accommodation_alternatives_heading', {
    placeName
  });
  const isShellOpen = isDesktop ? isOpen : isOpen && !isMapOpen;
  const mapSheetProps = {
    accommodations: accommodationList,
    currentAccommodationId,
    adultCount,
    childAges,
    updatingAccommodationId,
    onSelectAccommodation: (accommodation_0: AlternativeEntry) => selectAccommodation(accommodation_0, 'map'),
    onClose: () => setIsMapOpen(false),
    arrivalStation: alternatives?.arrivalStation,
    departureStation: alternatives?.departureStation
  };
  const searchInput = <label className={styles.searchInput}>
      <Search decorative size="small" />
      <input type="search" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder={t('accommodation_alternatives_search_placeholder')} aria-label={t('accommodation_alternatives_search_placeholder')} />
    </label>;
  const sortBySelect = <AriaSelect items={SORT_OPTIONS} ariaLabel={tAria('sort_by')} onSelectionChange={selected => setSortBy(selected as SortKeys)} selectedKey={sortBy} variant="pill">
      {item => <SelectItem key={item.id}>{t(item.labelKey)}</SelectItem>}
    </AriaSelect>;
  const seeOnMapButton = <button type="button" className={styles.pillButton} data-test-id="alternative-accommodation-see-on-map" onClick={() => {
    trackFunnelEvent('trip_funnel_hotel_see_on_map');
    setIsMapOpen(true);
  }}>
      <MapSolid decorative size="small" />
      {t('accommodation_see_on_map')}
    </button>;
  const filtersRow = <div className={styles.filtersRow}>
      {sortBySelect}
      {seeOnMapButton}
    </div>;
  const emptyMessage = accommodationList.length === 0 ? t('accommodation_alternatives_empty') : t('accommodation_alternatives_search_empty');
  const hasNoAlternativesToShow = accommodationList.length === 0 || filteredAccommodationList.length === 0;
  const alternativesPlaceholder = isLoading ? <div className={styles.modalContentLoading} aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('accommodation_loading_stays')}</span>
      <div className={styles.loadingBar} />
    </div> : <p className={clsx(styles.empty, isDesktop && styles.emptyModal)}>{emptyMessage}</p>;
  const alternativesList = <ul className={clsx(styles.list, isDesktop && styles.listModal)}>
      {displayedAccommodationList.map((alt: AlternativeEntry) => <li key={alt.id} className={styles.listItem}>
          <AlternativeAccommodationCard accommodation={alt} currentAccommodationId={currentAccommodationId} adultCount={adultCount} childAges={childAges} updatingAccommodationId={updatingAccommodationId} onSelect={(accommodation_1: AlternativeEntry) => selectAccommodation(accommodation_1, 'list')} />
        </li>)}
    </ul>;
  const alternativesContent = isLoading || hasNoAlternativesToShow ? alternativesPlaceholder : alternativesList;
  const drawerBody = isDesktop ? <div className={clsx(styles.drawerBody, styles.drawerBodyModal)}>
      <h2 className={clsx(styles.heading, styles.headingModal, isMapOpen && styles.headingModalMap)}>
        {t('accommodation_alternatives_heading', {
        placeName
      })}
      </h2>
      <Divider variant="horizontal" className={styles.dividerInset} />
      {isMapOpen ? null : <>
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <label className={clsx(styles.searchInput, styles.searchInputModal)}>
                <Search decorative size="small" />
                <input type="search" value={searchQuery} onChange={event_0 => setSearchQuery(event_0.target.value)} placeholder={t('accommodation_alternatives_search_placeholder')} aria-label={t('accommodation_alternatives_search_placeholder')} />
              </label>
              {sortBySelect}
            </div>
            {seeOnMapButton}
          </div>
          <Divider variant="horizontal" className={styles.dividerInset} />
        </>}
      <div className={clsx(styles.scrollArea, styles.scrollAreaModal, isMapOpen && styles.scrollAreaMap)}>
        {isMapOpen ? <AlternativeAccommodationMapSheetLazy variant="embedded" {...mapSheetProps} /> : alternativesContent}
      </div>
    </div> : <div className={styles.drawerBody}>
      <h2 className={styles.heading}>{t('accommodation_alternatives_heading', {
        placeName
      })}</h2>
      {searchInput}
      {filtersRow}
      <div className={styles.filtersDivider}>
        <Divider variant="horizontal" />
      </div>
      {alternativesContent}
    </div>;
  return <>
      {isDesktop ? <CenteredModal ariaLabel={ariaLabel} close={handleDesktopModalClose} closeDataTestId={isMapOpen ? 'AlternativeAccommodationModalBack' : 'AlternativeAccommodationModalClose'} closeVariant={isMapOpen ? 'back' : 'close'} dataTestId="AlternativeAccommodationModal" isOpen={isShellOpen} size="large">
          {drawerBody}
        </CenteredModal> : <OverlayDrawer isOpen={isShellOpen} close={handleClose} ariaLabel={ariaLabel} dataTestId="AlternativeAccommodationDrawer">
          {drawerBody}
        </OverlayDrawer>}
      {!isDesktop && isMapOpen ? <AlternativeAccommodationMapSheetLazy variant="portal" {...mapSheetProps} /> : null}
    </>;
};